import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, RefreshCw, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/pricing";
import { rememberOrderToken } from "@/lib/recentOrders";
import { CombineUnpaid } from "@/components/CombineUnpaid";
import { SupportBox } from "@/components/SupportLinks";
import { useCopy } from "@/lib/copy";

const COIN_NAMES = { BTC: "Bitcoin", LTC: "Litecoin" };
const STATUS_LABEL = {
  awaiting_payment: "Awaiting Payment",
  confirming: "Confirming on-chain…",
  paid: "Payment Received",
  failed: "Payment Failed",
};

const orderItemLabel = (item) => {
  if (item.type === "single" || item.type === "offer") {
    return `${item.quantity}× ${item.flavor_name} — ${item.size_label} (${item.base})${item.type === "offer" ? " · Checkout offer" : ""}`;
  }
  if (item.type === "multipack") {
    return `${item.quantity}× ${item.multipack_name || "Multipack"}`;
  }
  if (item.type === "guest") {
    const opts = (item.options || []).map((o) => o.label).filter(Boolean).join(", ");
    return `Premium · ${item.quantity}× ${item.flavor_name}${item.size_label ? ` — ${item.size_label}` : ""}${opts ? ` (${opts})` : ""}`;
  }
  return `${item.type === "economy" ? "Economy Flavours" : "Build Your Box"} · ${item.bottle_count || 0}× ${item.size_label || "Bottle"}`;
};

export default function OrderStatus() {
  const { C } = useCopy();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : "";
  const [order, setOrder] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${id}${tokenQuery}`);
      setOrder(response.data);
      setLoadError("");
      return response.data;
    } catch {
      setLoadError("This order could not be loaded. Check the link or contact support.");
      return null;
    }
  }, [id, tokenQuery]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { rememberOrderToken(id, token); }, [id, token]);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setChecking(true);
    try {
      const { data } = await api.post(`/orders/${id}/refresh-status${tokenQuery}`);
      const fresh = await load();
      if (!silent) {
        if (order?.payment_error) toast[fresh && !fresh.payment_error ? "success" : "error"](fresh && !fresh.payment_error ? "Payment details are ready" : "Payment still couldn't be set up — please try again shortly");
        else if (data.payment_status === "paid") toast.success("Payment confirmed!");
        else if (data.payment_status === "confirming") toast.info("Payment detected — confirming on-chain");
        else toast.info("No payment detected yet");
      }
    } catch {
      if (!silent) toast.error("Payment status could not be checked. Please try again.");
    } finally { if (!silent) setChecking(false); }
  }, [id, load, tokenQuery, order?.payment_error]);

  // Poll promptly while awaiting/confirming; BTCPay webhooks update the backend first.
  useEffect(() => {
    if (!order || order.payment_tracking_available === false || ["paid", "failed"].includes(order.payment_status)) return;
    const t = setInterval(() => refresh(true), 10000);
    return () => clearInterval(t);
  }, [order, refresh]);

  if (loadError && !order) return <div className="mx-auto flex h-[50vh] max-w-xl items-center justify-center px-6 text-center text-sm text-destructive">{loadError}</div>;
  if (!order) return <div className="flex h-[50vh] items-center justify-center mono text-xs uppercase tracking-widest text-muted-foreground">Loading…</div>;

  const paid = order.payment_status === "paid";
  const confirming = order.payment_status === "confirming";
  const failed = order.payment_status === "failed";
  const trackingUnavailable = order.payment_tracking_available === false;

  return (
    <div className="mx-auto max-w-3xl px-6 py-14">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Order {order.order_number}</div>
      <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter">{STATUS_LABEL[order.payment_status] || "Order"}</h1>

      <div className={`mt-6 flex items-center gap-3 border p-4 ${paid ? "border-green-600 bg-green-50 dark:bg-green-950/20" : failed ? "border-destructive" : "border-border"}`} data-testid="payment-status">
        {paid ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : failed ? <AlertTriangle className="h-5 w-5 text-destructive" /> : confirming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Clock className="h-5 w-5 text-muted-foreground" />}
        <span className="text-sm font-medium">
          {paid ? "Order confirmed — we're preparing your shipment." :
           failed ? "This payment failed or expired. Please place a new order." :
           trackingUnavailable ? "Payment tracking is unavailable. Please contact support." :
           confirming ? "Payment detected on the network — still unconfirmed. We are monitoring it automatically." :
           order.checkout_url ? `Complete your ${COIN_NAMES[order.currency] || order.currency} payment on our secure payment page.` :
           `Send exactly the amount below via ${order.crypto_network}.`}
        </span>
      </div>

      {order.tracking_pin && (
        <div className="mt-6 border border-dashed border-border bg-muted/30 p-4" data-testid="tracking-pin-box">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Save these to track your order</div>
          <p className="mt-2 text-sm">Order ID <span className="mono font-bold">{order.order_number}</span> · Tracking PIN <span className="mono font-bold">{order.tracking_pin}</span></p>
          <p className="mt-1 text-xs text-muted-foreground"><C k="status_pin_note" /></p>
        </div>
      )}
      <SupportBox />

      {order.payment_error && (
        <div className="mt-6 border border-destructive p-4 text-sm text-destructive" data-testid="payment-error">
          {order.payment_error}
          <div className="mt-3">
            <button type="button" onClick={() => refresh()} disabled={checking} data-testid="payment-retry-btn"
              className="border border-destructive px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-destructive hover:text-background disabled:opacity-50">
              {checking ? "Retrying…" : "Try again"}
            </button>
          </div>
        </div>
      )}

      {order.payment_group_id && !paid && (
        <div className="mt-6 border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="order-in-group">
          This order is part of a combined payment. <Link to={`/pay/${order.payment_group_id}`} className="underline">View the combined payment</Link> — paying it settles every order in the group.
        </div>
      )}

      {order.payment_status === "awaiting_payment" && !order.payment_group_id && <CombineUnpaid mode="browser" currentOrderId={order.id} />}

      {!paid && !failed && order.checkout_url && (
        <div className="mt-8 border border-border">
          <div className="mono border-b border-border bg-muted px-5 py-3 text-[10px] uppercase tracking-[0.2em]">
            Secure payment · {order.currency}
          </div>
          <div className="space-y-5 p-6">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Order total</div>
              <div className="mt-1 font-heading text-3xl font-black mono">{gbp(order.total)}</div>
            </div>
            <a
              href={order.checkout_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btcpay-checkout"
              className="flex w-full items-center justify-center bg-foreground py-4 text-sm font-medium text-background"
            >
              Open secure {COIN_NAMES[order.currency] || order.currency} payment →
            </a>
            <div className="border border-dashed border-border p-3 text-xs text-muted-foreground">
              Pay with <b>{COIN_NAMES[order.currency] || order.currency}</b> on our own secure payment page — no third party, funds go directly to our Ledger-protected wallet.
            </div>
            {confirming ? (
              <div className="border border-green-600/40 bg-green-50 p-4 text-center text-sm text-green-800 dark:bg-green-950/20 dark:text-green-300">
                Transaction found. No need to keep checking — this page updates automatically.
              </div>
            ) : (
              <button
                disabled={checking}
                onClick={() => refresh(false)}
                className="flex w-full items-center justify-center gap-2 border border-border py-4 text-sm font-medium"
              >
                <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
                {checking ? "Checking…" : "Check payment status"}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 border border-border">
        <div className="mono border-b border-border bg-muted px-5 py-3 text-[10px] uppercase tracking-[0.2em]">Items</div>
        <div className="divide-y divide-border">
          {order.items.map((it, i) => (
            <div key={`${it.type}-${it.flavor_id || it.multipack_id || "item"}-${i}`} className="flex justify-between p-4 text-sm">
              <span>{orderItemLabel(it)}</span>
              <span className="mono">{gbp(it.line_total ?? it.box_price ?? (it.unit_price || 0) * (it.quantity || 1))}</span>
            </div>
          ))}
          <div className="flex justify-between p-4 text-sm"><span>Shipping</span><span className="mono">{gbp(order.shipping)}</span></div>
          <div className="flex justify-between p-4 font-bold"><span>Total</span><span className="mono">{gbp(order.total)}</span></div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Link to={`/reorder/${order.id}${tokenQuery}`} data-testid="reorder-btn" className="inline-flex items-center gap-2 bg-foreground px-5 py-3 text-sm font-medium text-background transition-transform hover:-translate-y-0.5">
          <RefreshCw className="h-4 w-4" /> Order this again
        </Link>
        <Link to="/shop" className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">← Continue shopping</Link>
      </div>
    </div>
  );
}
